/**
 * Punctaje produse VAS/HVAS: import XLSX, tabel căutabil/editabil, aliasuri manuale,
 * raport produse HVAS nemapate. Punctajele alimentează calculul "Realizat" HVAS.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Upload, Search, Loader2, Save, Award, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const CHANNEL_LABELS = { telesales: "Telesales", d2d: "D2D", iasi: "Iași", bacau: "Bacău", cluj: "Cluj", oradea: "Oradea" };
const YEARS = [2024, 2025, 2026, 2027];

export default function ProductPointsPanel() {
  const { hasRole } = useAuth();
  const canWrite = hasRole(["admin", "manager", "backoffice", "bo"]);
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // item being edited

  // unmapped report
  const [umYear, setUmYear] = useState(2026);
  const [umChannel, setUmChannel] = useState("telesales");
  const [unmapped, setUnmapped] = useState(null);
  const [umLoading, setUmLoading] = useState(false);

  // punctaje fallback pe categorie
  const [catDefaults, setCatDefaults] = useState(null);
  const [savingCat, setSavingCat] = useState(null);

  const fetchCatDefaults = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/product-points/category-defaults`);
      setCatDefaults(r.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchCatDefaults(); }, [fetchCatDefaults]);

  const saveCatDefault = async (category, points) => {
    setSavingCat(category);
    try {
      await axios.put(`${API_URL}/product-points/category-default`, { category, points });
      toast.success(`Punctaj implicit „${category}" salvat`);
      fetchCatDefaults();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Nu ai permisiune" : "Salvarea a eșuat");
    } finally {
      setSavingCat(null);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category !== "all") params.category = category;
      const r = await axios.get(`${API_URL}/product-points`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      if (r.data.categories) setCategories(r.data.categories);
    } catch (e) {
      toast.error("Nu am putut încărca punctajele");
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
  }, [fetchList]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API_URL}/product-points/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import reușit: ${r.data.created} noi · ${r.data.updated} actualizate · ${r.data.skipped} sărite`);
      fetchList();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Importul a eșuat";
      toast.error(typeof msg === "string" ? msg : "Importul a eșuat");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const fetchUnmapped = useCallback(async () => {
    setUmLoading(true);
    try {
      const r = await axios.get(`${API_URL}/product-points/hvas-unmapped`, {
        params: { year: umYear, channel: umChannel },
      });
      setUnmapped(r.data);
    } catch (e) {
      toast.error("Nu am putut încărca raportul nemapate");
    } finally {
      setUmLoading(false);
    }
  }, [umYear, umChannel]);

  return (
    <div className="space-y-6" data-testid="product-points-panel">
      {/* Import + toolbar */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Award className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <CardTitle className="text-lg">Punctaje produse (VAS/HVAS)</CardTitle>
                <CardDescription>
                  {total} produse · punctajele alimentează „Realizat" HVAS din obiective
                </CardDescription>
              </div>
            </div>
            {canWrite && (
              <div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
                       onChange={handleUpload} data-testid="points-import-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="points-import-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Importă XLSX
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Caută denumire sau cod produs…" value={search}
                     onChange={(e) => setSearch(e.target.value)} className="pl-9"
                     data-testid="points-search" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-52" data-testid="points-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate categoriile</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b bg-slate-50">
                  <th className="px-3 py-2 font-semibold">Cod</th>
                  <th className="px-3 py-2 font-semibold">Denumire produs (SF)</th>
                  <th className="px-3 py-2 font-semibold">Categorie</th>
                  <th className="px-3 py-2 font-semibold text-center">Puncte</th>
                  <th className="px-3 py-2 font-semibold">Canale</th>
                  <th className="px-3 py-2 font-semibold text-center">Aliasuri</th>
                  <th className="px-3 py-2 font-semibold text-center">Activ</th>
                  {canWrite && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-400" data-testid="points-empty">Niciun produs. Importă fișierul XLSX.</td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} className={`border-b hover:bg-slate-50/60 ${!it.active ? "opacity-50" : ""}`} data-testid={`points-row-${it.product_code}`}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{it.product_code}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{it.sf_product_name}</td>
                    <td className="px-3 py-2 text-slate-600">{it.category || "—"}</td>
                    <td className="px-3 py-2 text-center font-bold text-amber-700" data-testid={`points-value-${it.product_code}`}>{it.points}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(it.channels || []).length === 0 ? <span className="text-slate-400 text-xs">—</span> :
                          (it.channels || []).slice(0, 3).map((c) => (
                            <Badge key={c} variant="secondary" className="text-[10px] px-1.5 py-0">{CHANNEL_LABELS[c] || c}</Badge>
                          ))}
                        {(it.channels || []).length > 3 && <span className="text-[10px] text-slate-400">+{it.channels.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(it.name_aliases || []).length > 0
                        ? <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{it.name_aliases.length}</Badge>
                        : <span className="text-slate-300 text-xs">0</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {it.active ? <span className="text-emerald-600 text-xs font-medium">Da</span> : <span className="text-slate-400 text-xs">Nu</span>}
                    </td>
                    {canWrite && (
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditing({ ...it, aliasesText: (it.name_aliases || []).join("\n") })}
                                data-testid={`points-edit-${it.product_code}`}>Editează</Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Punctaje fallback pe categorie */}
      {canWrite && catDefaults && (
        <CategoryDefaultsCard
          catDefaults={catDefaults}
          savingCat={savingCat}
          onSave={saveCatDefault}
        />
      )}

      {/* Unmapped report */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <CardTitle className="text-base">Produse HVAS fără punctaj mapat</CardTitle>
          </div>
          <CardDescription>Denumiri de produse din vânzări HVAS care nu au un punctaj asociat — adaugă un alias în produsul potrivit pentru a le include în „Realizat".</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <Select value={String(umYear)} onValueChange={(v) => setUmYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="unmapped-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={umChannel} onValueChange={setUmChannel}>
              <SelectTrigger className="w-44" data-testid="unmapped-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CHANNEL_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchUnmapped} disabled={umLoading} data-testid="unmapped-load">
              {umLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Verifică
            </Button>
          </div>
          {unmapped && (
            <div data-testid="unmapped-result">
              <div className="flex flex-wrap gap-4 text-sm mb-3">
                <span className="text-emerald-700 font-medium">Mapate: {unmapped.mapped_contracts}</span>
                {unmapped.mapped_via_category > 0 && (
                  <span className="text-indigo-600 font-medium" title="Denumiri generice mapate automat pe baza categoriei cu punctaj uniform (AC=9, CT=11)">
                    din care pe categorie: {unmapped.mapped_via_category}
                  </span>
                )}
                {unmapped.mapped_via_default > 0 && (
                  <span className="text-violet-600 font-medium" title="Mapate cu punctajul implicit pe categorie (ex. Electrocasnice)">
                    din care punctaj implicit: {unmapped.mapped_via_default}
                  </span>
                )}
                <span className="text-red-600 font-medium">Nemapate: {unmapped.unmapped_contracts}</span>
              </div>
              {unmapped.unmapped.length === 0 ? (
                <p className="text-sm text-slate-400">Toate produsele HVAS sunt mapate. 🎉</p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-2">
                    Rămase nemapate (categorii cu punctaj variabil, ex. Electrocasnice) — deschide un produs și adaugă denumirea ca alias pentru a le include:
                  </p>
                  <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                  {unmapped.unmapped.map((u, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span className="text-slate-700">{u.product_name || <em className="text-slate-400">(gol)</em>}</span>
                      <Badge variant="secondary">{u.count}</Badge>
                    </div>
                  ))}
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <EditDialog editing={editing} setEditing={setEditing} onSaved={fetchList} />
    </div>
  );
}

function CategoryDefaultsCard({ catDefaults, savingCat, onSave }) {
  const { uniform = {}, defaults = {}, variable_categories = [] } = catDefaults;
  const [drafts, setDrafts] = useState({});

  const uniformEntries = Object.entries(uniform);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Punctaj fallback pe categorie</CardTitle>
        <CardDescription>
          Denumirile generice (din CRM Legacy, fără model exact) primesc punctajul categoriei.
          AC/CT sunt automate (punctaj uniform); pentru categoriile cu punctaj variabil (ex. Electrocasnice) setezi tu un punctaj implicit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {uniformEntries.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="uniform-categories">
            {uniformEntries.map(([cat, pts]) => (
              <Badge key={cat} className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                {cat}: {pts} pct (auto)
              </Badge>
            ))}
          </div>
        )}
        <div className="space-y-2">
          {variable_categories.length === 0 ? (
            <p className="text-sm text-slate-400">Nicio categorie cu punctaj variabil.</p>
          ) : variable_categories.map((cat) => {
            const current = defaults[cat];
            const draftVal = drafts[cat] ?? (current ?? "");
            return (
              <div key={cat} className="flex items-center gap-3 flex-wrap" data-testid={`cat-default-row-${cat}`}>
                <span className="text-sm font-medium text-slate-700 w-40">{cat}</span>
                <Input
                  type="number" min={0}
                  className="w-24 h-8"
                  placeholder="fără"
                  value={draftVal}
                  onChange={(e) => setDrafts((d) => ({ ...d, [cat]: e.target.value }))}
                  data-testid={`cat-default-input-${cat}`}
                />
                <span className="text-xs text-slate-400">puncte / produs generic</span>
                <Button
                  size="sm" variant="outline" className="h-8"
                  disabled={savingCat === cat}
                  onClick={() => onSave(cat, draftVal === "" ? null : parseInt(draftVal, 10))}
                  data-testid={`cat-default-save-${cat}`}
                >
                  {savingCat === cat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                </Button>
                {current != null && (
                  <Badge variant="secondary" className="text-[10px]">activ: {current} pct</Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


function EditDialog({ editing, setEditing, onSaved }) {
  const [saving, setSaving] = useState(false);
  if (!editing) return null;

  const save = async () => {
    setSaving(true);
    try {
      const aliases = (editing.aliasesText || "").split("\n").map((s) => s.trim()).filter(Boolean);
      await axios.put(`${API_URL}/product-points/${editing.id}`, {
        points: Math.max(parseInt(editing.points, 10) || 0, 0),
        active: editing.active,
        name_aliases: aliases,
      });
      toast.success("Produs actualizat");
      setEditing(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Nu ai permisiune" : "Salvarea a eșuat");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent data-testid="points-edit-dialog">
        <DialogHeader>
          <DialogTitle>{editing.sf_product_name}</DialogTitle>
          <DialogDescription>Cod: {editing.product_code} · {editing.category || "fără categorie"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Puncte</label>
              <Input type="number" min={0} value={editing.points}
                     onChange={(e) => setEditing((p) => ({ ...p, points: e.target.value }))}
                     className="w-28" data-testid="points-edit-value" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500 block">Activ</label>
              <Switch checked={editing.active} onCheckedChange={(v) => setEditing((p) => ({ ...p, active: v }))}
                      data-testid="points-edit-active" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Aliasuri denumire (câte una pe linie)</label>
            <Textarea rows={5} value={editing.aliasesText}
                      placeholder={"AER CONDITIONAT\nMASINA DE SPALAT"}
                      onChange={(e) => setEditing((p) => ({ ...p, aliasesText: e.target.value }))}
                      data-testid="points-edit-aliases" />
            <p className="text-[11px] text-slate-400">Aliasurile mapează denumirile generice din vânzări la acest produs (case-insensitive).</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditing(null)}>Anulează</Button>
          <Button onClick={save} disabled={saving} data-testid="points-edit-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
